import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { reportError } from '../lib/sentry';
import {
  UNDO_WINDOW_MS,
  UndoToastContext,
  type UndoToastContextValue,
  type UndoableAction,
} from './undoToastContext';

/** What the stack renders. The action itself stays out of render state. */
interface PendingToast {
  id: string;
  label: string;
  verb: string;
}

interface RegistryEntry {
  action: UndoableAction;
  timer: ReturnType<typeof setTimeout>;
}

/**
 * Deferred-write host: schedules an action, shows a ticket with an Undo button
 * and a draining window, and only then lets the write reach the API.
 *
 * The commit is deliberately hard to lose. It fires when the window expires,
 * when the user dismisses the ticket, when the component that scheduled it
 * unmounts (see `useUndoableAction`), when this provider tears down, and on
 * `pagehide` — a closed tab or a refresh still writes.
 */
export function UndoToastProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<PendingToast[]>([]);
  const registry = useRef(new Map<string, RegistryEntry>());

  /** Cancels the timer and clears the ticket, handing back the action. */
  const forget = useCallback((id: string): UndoableAction | null => {
    const entry = registry.current.get(id);
    if (!entry) return null;
    clearTimeout(entry.timer);
    registry.current.delete(id);
    setPending((current) => current.filter((toast) => toast.id !== id));
    return entry.action;
  }, []);

  const commitNow = useCallback(
    (id: string) => {
      const action = forget(id);
      if (!action) return;
      void (async () => {
        try {
          await action.onCommit();
        } catch (caught) {
          reportError(caught, { scope: 'undoable_action_commit', label: action.label });
          action.onFailure?.(caught);
        }
      })();
    },
    [forget],
  );

  const undo = useCallback(
    (id: string) => {
      forget(id)?.onUndo();
    },
    [forget],
  );

  const schedule = useCallback(
    (action: UndoableAction): string => {
      const id = crypto.randomUUID();
      const timer = setTimeout(() => commitNow(id), UNDO_WINDOW_MS);
      registry.current.set(id, { action, timer });
      // Newest first: the stack is anchored to the bottom of the viewport.
      setPending((current) => [
        { id, label: action.label, verb: action.verb ?? 'deleted' },
        ...current,
      ]);
      return id;
    },
    [commitNow],
  );

  const commitAll = useCallback(() => {
    for (const id of [...registry.current.keys()]) commitNow(id);
  }, [commitNow]);

  useEffect(() => {
    // `pagehide` is the one teardown event that fires for tab close, refresh
    // and back/forward cache alike. The DELETE goes out with `keepalive`, so
    // the request survives the page that scheduled it. Best effort, not a
    // guarantee: the client still awaits the Supabase session before it can
    // send, and the browser may tear the page down first.
    const flush = () => commitAll();
    window.addEventListener('pagehide', flush);
    return () => {
      window.removeEventListener('pagehide', flush);
      // Provider teardown (sign-out, app unmount) follows the same rule.
      commitAll();
    };
  }, [commitAll]);

  const value = useMemo<UndoToastContextValue>(
    () => ({ schedule, commitNow }),
    [schedule, commitNow],
  );

  return (
    <UndoToastContext.Provider value={value}>
      {children}

      {/* The region is always mounted so screen readers announce inserts. */}
      <div className="undo-stack" role="status" aria-live="polite">
        {pending.map((toast) => (
          <article className="undo-toast" key={toast.id}>
            <p className="undo-toast__message">
              <span className="undo-toast__label">“{toast.label}”</span> {toast.verb}
            </p>
            <div className="undo-toast__actions">
              <button
                className="button button--ghost undo-toast__undo"
                type="button"
                onClick={() => undo(toast.id)}
              >
                Undo
              </button>
              <button
                className="undo-toast__close"
                type="button"
                aria-label={`Dismiss and delete “${toast.label}” now`}
                onClick={() => commitNow(toast.id)}
              >
                ×
              </button>
            </div>
            {/* CSS-driven countdown: the window drains without re-rendering. */}
            <span
              className="undo-toast__drain"
              style={{ animationDuration: `${UNDO_WINDOW_MS}ms` }}
              aria-hidden="true"
            />
          </article>
        ))}
      </div>
    </UndoToastContext.Provider>
  );
}
